import { DataTable } from "@/components/shared";
import { JSX, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { getColumnsCars } from "../Cars/columns";
import { ConfirmRemoveAlert } from "../Dialog";
import { useParams } from "react-router-dom";
import { CarDetails } from "@/graphql/__generated__/graphql";
import { useRemoveCarMutationHook } from "@/hooks/mutations";
import AddCar from "./Form";
import { useFetchCarsOnTenderQueryHook, useFetchServicesOnTenderQueryHook } from "@/hooks/queries";

interface AddCarComponentProps {
  /** Optional cars data passed from parent (e.g., from a Service) */
  cars?: CarDetails[];
}

/**
 * Add Car component. Acts as a wrapper for the AddCar form.
 * Can display cars from props (e.g., linked to a Service) or fetch from tender.
 *
 * @returns {JSX.Element}
 */
const AddCarComponent = ({ cars }: AddCarComponentProps): JSX.Element => {

  // States
  const [showAddCarModal, setShowAddCarModal] = useState<boolean>(false);
  const [showConfirmRemoveAlert, setShowConfirmRemoveAlert] = useState<boolean>(false);
  const [currentCarID, setCurrentCarID] = useState<number | undefined>();

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["translation", "common", "components"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  // Mutation hooks
  const { removeCarMutation, loading: removeCarLoading } = useRemoveCarMutationHook();

  // Fetch cars from tender if not provided via props
  const { data: carsData, loading: carsLoading } = useFetchCarsOnTenderQueryHook(Number(tenderID));
  const { data: servicesData } = useFetchServicesOnTenderQueryHook(Number(tenderID), false, true);

  // Use cars from props if provided, otherwise use fetched data
  const displayCars = cars ?? (carsData?.fetchCarsOnTender ?? []) as CarDetails[];
  const services = servicesData?.fetchServicesOnTender?.services ?? [];

  /**
   * OnClick handler for the Remove button in the Cars table
   * @param {CarDetails} carDetails - The current car details to be removed
   */
  const handleClickRemove = (carDetails: CarDetails) => {
    setCurrentCarID(carDetails.car.id);
    setShowConfirmRemoveAlert(true);
  };

  /**
   * Handler function for `removeCarMutation`
   */
  const handleRemoveCar = () => {
    removeCarMutation({
      variables: {
        tenderId: Number(tenderID),
        carId: Number(currentCarID),
      }
    });
  };

  return (
    <div>
      <DataTable
        headerStyling="font-normal text-sm"
        disableRowColour={true}
        loading={!cars && carsLoading}
        displayAmount={displayCars.length || 10}
        data={displayCars}
        columns={getColumnsCars(intl, t, false, () => {}, handleClickRemove, removeCarLoading, services)}
        className="max-h-120"
      />
      <AddCar open={showAddCarModal} onOpenChange={setShowAddCarModal}/>

      {/* Confirm Remove Car */}
      <ConfirmRemoveAlert
        open={showConfirmRemoveAlert}
        onOpenChange={setShowConfirmRemoveAlert}
        actionButtonFn={handleRemoveCar}
        bodyText={t("components:alertdialogs.removecar")}
      />
    </div>
  );
};

export default AddCarComponent;
